import { Link } from 'react-router';
import { COMPANY, SITE } from '@/content/company';
import { Clause, LegalPage } from './LegalPage';
import legal from './LegalPage.module.css';

/**
 * Accessibility statement.
 *
 * Framed throughout as a *voluntary* conformance claim, and that framing is
 * deliberate. Directive (EU) 2019/882 (the European Accessibility Act) does not
 * bind this company on two independent grounds: the microenterprise exemption
 * covers service providers under 10 staff and under €2M turnover, and the
 * directive's scope is consumer-facing e-commerce rather than a B2B marketing
 * site. Claiming to comply with a directive that exempts you invites a question
 * you do not need to answer, and misstates your legal position.
 *
 * The work behind the claim is real and already done - see
 * docs/DESIGN-FIDELITY.md - so the page exists to make it visible rather than
 * to promise anything new.
 */

const TOC = [
  '1. What we claim',
  '2. How it was tested',
  '3. What we did',
  '4. Known limitations',
  '5. Legal position',
  '6. Feedback',
] as const;

export function Accessibility() {
  return (
    <LegalPage
      eyebrow="Accessibility"
      title="Accessibility Statement"
      toc={TOC}
      intro={
        <>
          {COMPANY.legalName} wants skabro.eu to be usable by everyone, including people who browse
          with a keyboard, a screen reader, magnification, high-contrast mode or reduced motion.
          This statement sets out what we claim, how we tested it, and what we know is imperfect.
        </>
      }
    >
      <Clause n={1} title="What we claim">
        <p className={legal.pairs}>
          skabro.eu is designed and built to conform with the{' '}
          {/* Same tab on purpose: opening a new one unannounced is itself a
              usability problem, and warning about it would need visible text
              this sentence does not want. */}
          <a href="https://www.w3.org/TR/WCAG22/">
            Web Content Accessibility Guidelines (WCAG) 2.2
          </a>{' '}
          at <strong>level AA</strong>, which is the technical standard referenced by EN 301 549 and
          by accessibility law across the EU.
        </p>
        <p>
          We believe the site fully conforms at that level. &ldquo;Fully conforms&rdquo; means every
          page we publish meets every level A and level AA success criterion, so far as we have been
          able to establish by the testing described below. It is a considered claim rather than an
          aspiration, and clause 4 says where we think it could still be wrong.
        </p>
      </Clause>

      <Clause n={2} title="How this was tested">
        <p className={legal.continues}>
          Conformance was evaluated by self-assessment, combining automated and manual testing:
        </p>
        <ul>
          <li>
            <strong>Automated.</strong> An axe-core sweep runs over every page of the site at three
            viewport widths - 1440px, 768px and 375px - plus the open mobile menu, the contact
            form&rsquo;s error state and its confirmation state. It covers the WCAG 2.0, 2.1 and 2.2
            A and AA rule sets. It currently reports zero violations, and it runs in continuous
            integration on every change, so a regression fails the build rather than reaching the
            site.
          </li>
          <li>
            <strong>Manual.</strong> Keyboard-only navigation and tab order, focus visibility at
            every interactive stop, skip-link behaviour, in-page anchor targets, the mobile
            disclosure menu, form error handling and focus management, reflow at 320px, reduced
            motion, heading structure and landmark structure were each checked by hand.
          </li>
          <li>
            <strong>Colour contrast.</strong> Every text and control colour was measured against the
            surface it sits on. Four values inherited from the original design fell below the
            required ratios and were corrected: body text at 4.47:1, a code comment at 3.93:1, a
            gutter at 2.43:1, and control borders at 1.66:1 and 1.89:1. All now clear the 4.5:1
            threshold for text and 3:1 for control boundaries.
          </li>
          <li>
            <strong>Linting.</strong> Accessibility lint rules run at error level in the build, so
            common markup mistakes cannot be committed.
          </li>
        </ul>
        <p>Last evaluated: {SITE.legalLastUpdated}.</p>
      </Clause>

      <Clause n={3} title="What the site does">
        <p className={legal.continues}>
          Some specifics, since a conformance claim on its own is hard to check:
        </p>
        <ul>
          <li>
            Every page is server-rendered to static HTML, so it works with JavaScript unavailable or
            blocked.
          </li>
          <li>
            A skip link is the first focusable element on every page, and it moves focus rather than
            only scrolling.
          </li>
          <li>
            Every interactive element has a visible focus indicator. None were removed for visual
            reasons.
          </li>
          <li>
            Nothing relies on colour alone. The current page is marked with{' '}
            <code>aria-current</code> as well as colour, and links inside prose are underlined
            because teal on grey does not clear the required 3:1 against surrounding text.
          </li>
          <li>
            All motion is decorative, respects <code>prefers-reduced-motion</code>, and is
            regression tested - including smooth scrolling, which is disabled under the same
            preference.
          </li>
          <li>
            Pointer targets meet the 24px minimum, and text reflows to 320px without horizontal
            scrolling.
          </li>
          <li>
            Client-side navigation moves focus to the main landmark and announces the new page, so a
            screen reader is not left silent by a route change that produces no page load.
          </li>
        </ul>
      </Clause>

      <Clause n={4} title="Known limitations">
        <p>
          Automated tools detect somewhere around a third of accessibility problems. The rest depend
          on judgement - whether alternative text is genuinely useful, whether reading order makes
          sense, whether an interaction is understandable - and our manual testing, while thorough,
          was carried out by the team that built the site rather than by an independent auditor or
          by people who rely on assistive technology daily.
        </p>
        <p>
          The site has not been tested with every combination of browser and screen reader in use.
          It has not been through a third-party accessibility audit or a user-testing round with
          disabled participants. We would rather say that plainly than imply a level of assurance we
          have not bought.
        </p>
        <p>If you hit a barrier, it is a defect and we want to know - see clause 6.</p>
      </Clause>

      <Clause n={5} title="Our legal position">
        <p>
          Directive (EU) 2019/882, the European Accessibility Act, has applied since 28 June 2025.
          It does not impose obligations on {COMPANY.legalName} in respect of this website, for two
          independent reasons: the Act exempts microenterprises providing services - fewer than ten
          people and under €2 million turnover - and its scope covers consumer-facing e-commerce
          services rather than an informational business-to-business website.
        </p>
        <p>
          We publish this statement anyway. The work was worth doing on its own terms, and an
          accessibility claim nobody can read is not much of a claim. Nothing here should be taken
          as an admission that the Act applies to us, and nothing here limits any right you have
          under applicable law.
        </p>
      </Clause>

      <Clause n={6} title="Feedback and contact">
        <p>
          If any part of this site is difficult or impossible to use, please write to{' '}
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. Tell us the page, what you were
          trying to do, and what happened - and, if you can, the browser and any assistive
          technology you were using. We aim to acknowledge accessibility reports within one working
          day and to tell you what we intend to do about it.
        </p>
        {/*
         * Email and post are given here unconditionally, and the phone is a link
         * away rather than printed.
         *
         * Worth being explicit about the reasoning, because this is the one page
         * where hiding a contact route would be the wrong instinct. The number now
         * requires JavaScript and a reachable Google to obtain, which is a worse
         * channel than email for exactly the visitor this statement is addressed
         * to - so email leads, and it is the channel the feedback duty is actually
         * satisfied by. Post is unconditional too. Nothing here depends on the
         * reveal working.
         */}
        <p>
          You can also reach us by post at {COMPANY.legalName}, {COMPANY.address.street},{' '}
          {COMPANY.address.localityFormal}, {COMPANY.address.country}, or by telephone - the number
          is on our <Link to="/contact">contact page</Link>.
        </p>
        <p>
          This statement was prepared on {SITE.legalLastUpdated} by self-assessment, and is reviewed
          whenever the site changes materially.
        </p>
      </Clause>
    </LegalPage>
  );
}
